import { au13RulesFor, calculateAu13 } from "../au13/index.js";
import { calculateIncomeTax, incomeTaxRulesFor } from "../income-tax/index.js";
import type { FinancialYear } from "../types.js";
import {
  type FtbAEntitlement,
  type FtbATaperRules,
  type SecondEarnerChild,
  type SecondEarnerDay,
  type SecondEarnerInput,
  SecondEarnerInputSchema,
  type SecondEarnerOutput,
  type SecondEarnerParsed,
  type SecondEarnerRuleSet,
} from "./types.js";

// ---------------------------------------------------------------------------
// Pure calculation for the second-earner "extra day" surface. No I/O, no
// Date.now(); the assessment FY (and so every ruleset) is always a parameter.
//
// Each modelled day is decomposed MARGINALLY against the prior day's totals, so
// the per-day net reflects exactly which taper that day's gross fell into:
//   • income tax + Medicare levy + HELP — via the shared income-tax engine,
//     stacked on the second earner's OWN income (they are taxed individually).
//   • FTB Part A withdrawal — via the two-stage family income test, the rise in
//     COMBINED income from this day stripping max-rate then base-rate FTB-A.
//   • extra out-of-pocket childcare — via the shipped au13 CCS engine recomputed
//     at the raised COMBINED income; the same care costs more as CCS% steps down.
//
// HARD RULE (binding ship-condition): this surface NEVER produces a "worth it /
// not worth working" verdict. The output carries gross beside net and a neutral
// keep-rate only. `assertNoVerdict` enforces that no verdict field/string can
// leak into the result; a test asserts it.
// ---------------------------------------------------------------------------

/** Total tax (income tax + Medicare levy + compulsory HELP) on a resident's
 * taxable income, via the shared income-tax engine. Used for marginal stacking
 * — the second earner's day income stacks on their own prior income. */
function totalTaxFor(
  taxableIncome: number,
  hasStudyLoan: boolean,
  fy: FinancialYear,
): { tax: number; help: number } {
  const rules = incomeTaxRulesFor(fy);
  const out = calculateIncomeTax(
    { taxableIncome, residency: "resident", hasStudyLoan, assessmentYear: fy },
    rules,
  );
  // netTax = income tax (after LITO/SAPTO) + Medicare levy; helpRepayment is the
  // separate compulsory study-loan line. Keep them apart so the day's HELP slice
  // is reported on its own.
  return { tax: out.netTax, help: out.helpRepayment };
}

/**
 * FTB Part A REMAINING at a combined family income — i.e. the family's annual
 * FTB-A after the two-stage income test withdraws it. Pure function of income +
 * the family's own max/base entitlement + the verified taper. The day's
 * withdrawal is then `remaining(before) − remaining(after)` (always ≥ 0).
 *
 * Stage 1: the MAX-rate component reduces 20c per $1 above the lower free area,
 *          but never below the base-rate component (the family floors at base).
 * Stage 2: the BASE-rate component reduces 30c per $1 above the higher free
 *          area, floored at $0.
 * The two stages run on the SAME income (Services Australia applies whichever
 * leaves the higher payment; for the standard case this is the max-rate path
 * down to base, then the base-rate path) — we take the max-rate result but never
 * below base, then taper base separately, matching the published two-step test.
 */
export function ftbARemaining(
  combinedIncome: number,
  entitlement: FtbAEntitlement,
  taper: FtbATaperRules,
): number {
  const { maxRateAnnual, baseRateAnnual } = entitlement;

  // Stage 1 — max-rate path: full max entitlement, reduced 20c/$1 above the
  // lower free area, but not below the base-rate component.
  let stage1 = maxRateAnnual;
  if (combinedIncome > taper.lowerFreeArea) {
    const reduction = taper.lowerTaperRate * (combinedIncome - taper.lowerFreeArea);
    stage1 = Math.max(baseRateAnnual, maxRateAnnual - reduction);
  }

  // Stage 2 — base-rate path: the base component reduced 30c/$1 above the higher
  // free area, floored at 0.
  let stage2base = baseRateAnnual;
  if (combinedIncome > taper.higherFreeArea) {
    const reduction = taper.higherTaperRate * (combinedIncome - taper.higherFreeArea);
    stage2base = Math.max(0, baseRateAnnual - reduction);
  }

  // The family receives whichever rate path leaves the higher payment, BUT once
  // stage 1 has tapered the max component down to (or below) base, the base
  // component itself starts its own taper above the higher free area. The
  // published result is: payment = max(base-after-stage2, max-after-stage1),
  // where stage1 already floors at base. Above the higher free area, stage1 sits
  // at base and stage2 erodes it — so the controlling value is stage2 there.
  if (combinedIncome > taper.higherFreeArea) {
    // base component is being withdrawn; the max-rate path also at base → use
    // the eroding base value.
    return Math.round(Math.min(stage1, stage2base) * 100) / 100;
  }
  return Math.round(stage1 * 100) / 100;
}

/** Total annual out-of-pocket childcare gap across all children at a given
 * combined family income, via the shipped au13 CCS engine (per-fortnight gap ×
 * fortnights/year). Pure — recomputes each child's CCS at this income. */
function annualChildcareGap(
  combinedIncome: number,
  children: readonly SecondEarnerChild[],
  fy: FinancialYear,
  fortnightsPerYear: number,
): number {
  if (children.length === 0) return 0;
  const ccsRules = au13RulesFor(fy);
  let fortnightlyGap = 0;
  for (const child of children) {
    const out = calculateAu13(
      {
        familyIncome: combinedIncome,
        hoursUsed: child.hoursUsed,
        careType: child.careType,
        hourlyFee: child.hourlyFee,
        activityHours: child.activityHours,
        schoolAged: child.schoolAged,
        indigenousChild: child.indigenousChild,
        childRank: child.childRank,
      },
      ccsRules,
    );
    fortnightlyGap += out.gapPerFortnight;
  }
  return Math.round(fortnightlyGap * fortnightsPerYear * 100) / 100;
}

/**
 * Walk days 1 → `daysToModel`, decomposing each day's gross into the marginal
 * tax / FTB / childcare it strips, and report the per-day net beside the gross.
 * Pure: same inputs + rulesets always yield the same output.
 */
export function calculateSecondEarnerDays(
  rawInput: SecondEarnerInput,
  rules: SecondEarnerRuleSet,
): SecondEarnerOutput {
  const input: SecondEarnerParsed = SecondEarnerInputSchema.parse(rawInput);
  const fy = input.assessmentYear as FinancialYear;

  const days: SecondEarnerDay[] = [];
  let totalGross = 0;
  let totalNet = 0;

  // --- Extra out-of-pocket childcare across the whole modelled span. ---
  // The au13 CCS applicable percentage is a STEP function (rounded to whole
  // percentage points, one step per ~$5,000 of income). Differencing it over a
  // single ~$300 day therefore yields $0 on most days and an arbitrary lump on
  // whichever day happens to straddle a $5,000 boundary — a non-monotonic,
  // misleading per-day waterfall. Instead we take the TOTAL childcare gap the
  // modelled days create (gap at the final combined income minus gap at the
  // starting combined income, both via the shipped au13 engine) and attribute it
  // SMOOTHLY as an equal slice per day. That reports the AVERAGE marginal CCS
  // step-down over the income range the user is modelling — monotonic and free of
  // the rounding artefact, while still reusing au13's CCS math wholesale.
  const combinedStart = input.primaryIncome + input.secondEarnerBaseIncome;
  const combinedEnd =
    input.primaryIncome + input.secondEarnerBaseIncome + input.dayGross * input.daysToModel;
  const totalExtraChildcare = Math.max(
    0,
    Math.round(
      (annualChildcareGap(combinedEnd, input.children, fy, rules.fortnightsPerYear) -
        annualChildcareGap(combinedStart, input.children, fy, rules.fortnightsPerYear)) *
        100,
    ) / 100,
  );
  const perDayChildcare =
    input.daysToModel > 0
      ? Math.round((totalExtraChildcare / input.daysToModel) * 100) / 100
      : 0;

  for (let day = 1; day <= input.daysToModel; day += 1) {
    // Second earner income BEFORE and AFTER this day is added.
    const seBefore = input.secondEarnerBaseIncome + input.dayGross * (day - 1);
    const seAfter = input.secondEarnerBaseIncome + input.dayGross * day;

    const combinedBefore = input.primaryIncome + seBefore;
    const combinedAfter = input.primaryIncome + seAfter;

    // --- Income tax + Medicare levy + HELP (marginal, on the second earner). ---
    const taxBefore = totalTaxFor(seBefore, input.hasStudyLoan, fy);
    const taxAfter = totalTaxFor(seAfter, input.hasStudyLoan, fy);
    const extraTax = Math.max(0, taxAfter.tax - taxBefore.tax);
    const extraHelp = Math.max(0, taxAfter.help - taxBefore.help);

    // --- FTB Part A withdrawal (marginal, on combined family income). ---
    const ftbBefore = ftbARemaining(combinedBefore, input.ftbAEntitlement, rules.ftbA);
    const ftbAfter = ftbARemaining(combinedAfter, input.ftbAEntitlement, rules.ftbA);
    const ftbWithdrawn = Math.max(0, Math.round((ftbBefore - ftbAfter) * 100) / 100);

    // --- Extra out-of-pocket childcare (CCS step-down) — an equal slice of the
    // span total (see the smoothing rationale above). On the LAST day, absorb the
    // rounding remainder so the per-day slices sum back to the span total exactly.
    const extraChildcare =
      day < input.daysToModel
        ? perDayChildcare
        : Math.round(
            (totalExtraChildcare - perDayChildcare * (input.daysToModel - 1)) * 100,
          ) / 100;

    const gross = input.dayGross;
    const net =
      Math.round((gross - extraTax - extraHelp - ftbWithdrawn - extraChildcare) * 100) /
      100;
    const keepRate = gross > 0 ? Math.max(0, Math.min(1, net / gross)) : null;

    days.push({
      day,
      gross,
      extraTax,
      extraHelp,
      ftbWithdrawn,
      extraChildcare,
      net,
      keepRate,
    });
    totalGross += gross;
    totalNet += net;
  }

  const out: SecondEarnerOutput = {
    primaryIncome: input.primaryIncome,
    secondEarnerBaseIncome: input.secondEarnerBaseIncome,
    dayGross: input.dayGross,
    days,
    totalGross: Math.round(totalGross * 100) / 100,
    totalNet: Math.round(totalNet * 100) / 100,
  };

  // Binding ship-condition guard: NO verdict may ever reach the output.
  assertNoVerdict(out);
  return out;
}

// ---------------------------------------------------------------------------
// Hard-rule guard: the output must NEVER carry a "worth it / not worth working"
// verdict — that is advice-drift AND misleading (it omits gross, super, career
// progression). This guard fails loudly if such a field/string ever leaks in.
// A test exercises it. Encoding the ship-condition as CODE, not a comment.
// ---------------------------------------------------------------------------

/** Field names that would constitute an advice-shaped verdict. Banned. */
const BANNED_VERDICT_KEYS = [
  "verdict",
  "worthIt",
  "worthit",
  "recommendation",
  "recommended",
  "shouldWork",
  "advice",
  "decision",
] as const;

/** Substrings that would constitute an advice-shaped verdict if emitted as a
 * string value anywhere in the output. */
const BANNED_VERDICT_PHRASES = [
  "worth it",
  "not worth",
  "worth working",
  "you should",
  "stay home",
  "don't work",
  "do not work",
  "work more",
] as const;

/**
 * Assert the result object carries no verdict field or verdict-shaped string.
 * Throws if the hard rule is violated. The keep-rate is a numeric mechanic and
 * is allowed; only verdict KEYS and verdict-shaped STRING values are banned.
 */
export function assertNoVerdict(value: unknown): void {
  const seen = new WeakSet<object>();
  const walk = (v: unknown): void => {
    if (typeof v === "string") {
      const lower = v.toLowerCase();
      for (const phrase of BANNED_VERDICT_PHRASES) {
        if (lower.includes(phrase)) {
          throw new Error(
            `second-earner hard-rule violation: verdict-shaped string "${v}" in output`,
          );
        }
      }
      return;
    }
    if (v === null || typeof v !== "object") return;
    if (seen.has(v)) return;
    seen.add(v);
    if (Array.isArray(v)) {
      for (const item of v) walk(item);
      return;
    }
    for (const [key, child] of Object.entries(v)) {
      if (BANNED_VERDICT_KEYS.includes(key.toLowerCase() as never)) {
        throw new Error(
          `second-earner hard-rule violation: banned verdict key "${key}" in output`,
        );
      }
      walk(child);
    }
  };
  walk(value);
}
